import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiRequest } from "../helpers/api";
import { ConversationSkeleton, SupportCategorySkeleton, SupportFaqSkeleton, TicketListSkeleton } from "../components/skeleton/PageSkeletons";
import { showError, showSuccess } from "../components/ToastNotifications";
import { downloadSupportAttachment, filesToPayload, supportFileAccept, validateSupportFiles } from "../helpers/support";
import "./HelpSupport.css";

const categories = [
  "Account & Profile",
  "AI Content Generation",
  "Content Scheduling",
  "Social Accounts",
  "Messages & Automations",
  "Leads",
  "Analytics",
  "Billing & Subscription",
  "Technical Issues"
];
const priorities = ["Low", "Normal", "High", "Urgent"];
const formatDate = (value) => value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

const StatusBadge = ({ status }) => <span className={`support-status support-status-${String(status || "").toLowerCase().replaceAll(" ", "-")}`}>{status}</span>;
const PriorityBadge = ({ priority }) => <span className={`support-priority support-priority-${String(priority || "").toLowerCase()}`}>{priority}</span>;

const HelpSupport = () => {
  const [faqs, setFaqs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [openFaq, setOpenFaq] = useState(null);
  const [sendingFeedback, setSendingFeedback] = useState(null);
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("Normal");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);
  const [replying, setReplying] = useState(false);
  const ticketFormRef = useRef(null);

  const loadPage = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [faqResult, ticketResult] = await Promise.all([
        apiRequest("/api/support/faqs"),
        apiRequest("/api/support/tickets")
      ]);
      setFaqs(faqResult.faqs || []);
      setTickets(ticketResult.tickets || []);
    } catch (error) {
      setLoadError(error.message || "Unable to load Help & Support.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPage(); }, []);
  useEffect(() => {
    if (!selectedTicket) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSelectedTicket(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selectedTicket]);

  const filteredFaqs = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return faqs;
    return faqs.filter((faq) => `${faq.question} ${faq.answer} ${faq.category}`.toLowerCase().includes(term));
  }, [faqs, search]);
  const faqGroups = useMemo(() => categories.map((name) => ({
    name,
    items: filteredFaqs.filter((faq) => faq.category === name)
  })).filter((group) => group.items.length), [filteredFaqs]);

  const chooseFiles = (event, target) => {
    const nextFiles = Array.from(event.target.files || []);
    event.target.value = "";
    const current = target === "reply" ? replyFiles : files;
    const validationError = validateSupportFiles(nextFiles, current);
    if (validationError) return showError(validationError);
    (target === "reply" ? setReplyFiles : setFiles)([...current, ...nextFiles]);
  };

  const submitTicket = async (event) => {
    event.preventDefault();
    if (!subject.trim() || !category || !description.trim()) return showError("Complete the subject, category, and description before submitting.");
    setSubmitting(true);
    try {
      const attachments = await filesToPayload(files);
      const result = await apiRequest("/api/support/tickets", {
        method: "POST",
        body: JSON.stringify({ subject: subject.trim(), category, priority, description: description.trim(), attachments })
      });
      setSubject("");
      setCategory("");
      setPriority("Normal");
      setDescription("");
      setFiles([]);
      await loadPage();
      showSuccess(`Ticket ${result.ticket.ticketNumber} was submitted successfully.`);
      openTicket(result.ticket.id);
    } catch (error) {
      showError(error.message || "Unable to submit your ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  const openTicket = async (id) => {
    setSelectedTicket(id);
    setTicketDetails(null);
    setTicketLoading(true);
    try {
      const result = await apiRequest(`/api/support/tickets/${id}`);
      setTicketDetails(result.ticket);
    } catch (error) {
      showError(error.message || "Unable to load ticket details.");
      setSelectedTicket(null);
    } finally {
      setTicketLoading(false);
    }
  };

  const sendReply = async (event) => {
    event.preventDefault();
    if (!reply.trim()) return showError("Write a message before replying.");
    setReplying(true);
    try {
      const attachments = await filesToPayload(replyFiles);
      await apiRequest(`/api/support/tickets/${selectedTicket}/replies`, {
        method: "POST",
        body: JSON.stringify({ message: reply.trim(), attachments })
      });
      setReply("");
      setReplyFiles([]);
      const result = await apiRequest(`/api/support/tickets/${selectedTicket}`);
      setTicketDetails(result.ticket);
      await loadPage();
      showSuccess("Your reply was sent.");
    } catch (error) {
      showError(error.message || "Unable to send your reply.");
    } finally {
      setReplying(false);
    }
  };

  const sendFeedback = async (faq, helpful) => {
    setSendingFeedback(faq.id);
    try {
      const result = await apiRequest(`/api/support/faqs/${faq.id}/feedback`, {
        method: "POST",
        body: JSON.stringify({ helpful })
      });
      setFaqs((items) => items.map((item) => item.id === faq.id ? { ...item, feedback: result.feedback } : item));
    } catch (error) {
      showError(error.message || "Unable to save feedback.");
    } finally {
      setSendingFeedback(null);
    }
  };

  return (
    <section className="support-page">
      <header className="support-hero">
        <div className="support-hero-copy">
          <span className="support-eyebrow"><i className="ti ti-sparkles" aria-hidden="true" /> SOCIALYORIX HELP CENTER</span>
          <h1>How can we help?</h1>
          <p>Find answers, explore helpful topics, or talk to our support team.</p>
          <label className="support-search">
            <i className="ti ti-search" aria-hidden="true" />
            <input aria-label="Search help articles" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search help articles..." />
            {search && <button type="button" onClick={() => setSearch("")} aria-label="Clear search"><i className="ti ti-x" /></button>}
          </label>
        </div>
        <div className="support-hero-art" aria-hidden="true"><span className="support-art-orbit" /><span className="support-art-icon"><i className="ti ti-lifebuoy" /></span><span className="support-art-spark support-art-spark-one">✦</span><span className="support-art-spark support-art-spark-two">✦</span></div>
      </header>

      {loadError && <div className="support-inline-error" role="alert">{loadError} <button type="button" onClick={loadPage}>Try again</button></div>}

      <div className="support-section-heading">
        <div><p className="support-eyebrow">QUICK HELP</p><h2>Browse by topic</h2><p>Choose a topic to see related answers.</p></div>
      </div>
      {loading ? <SupportCategorySkeleton /> : <div className="support-category-grid">
        {categories.map((name, index) => <button type="button" key={name} className="support-category" onClick={() => { setSearch(name); document.getElementById("support-faqs")?.scrollIntoView({ behavior: "smooth" }); }}>
          <span className={`support-category-icon support-category-icon-${index}`}><i className={`ti ${["ti-user-circle", "ti-sparkles", "ti-calendar-event", "ti-brand-instagram", "ti-message-circle", "ti-user-star", "ti-chart-bar", "ti-credit-card", "ti-tool"][index]}`} aria-hidden="true" /></span>
          <span><strong>{name}</strong><small>{faqs.filter((faq) => faq.category === name).length} articles</small></span><i className="ti ti-chevron-right support-category-arrow" aria-hidden="true" />
        </button>)}
      </div>}

      <section className="support-faq-section" id="support-faqs">
        <div className="support-section-heading support-faq-heading">
          <div><p className="support-eyebrow">KNOWLEDGE BASE</p><h2>Frequently asked questions</h2><p>Search concise answers from the Socialyorix team.</p></div>
          <span className="support-faq-count">{filteredFaqs.length} {filteredFaqs.length === 1 ? "article" : "articles"}</span>
        </div>
        {loading ? <SupportFaqSkeleton /> :
          faqGroups.length ? faqGroups.map((group) => <div className="support-faq-group" key={group.name}>
            <h3>{group.name}</h3>
            <div className="support-faq-list">{group.items.map((faq) => <article className={`support-faq-item${openFaq === faq.id ? " is-open" : ""}`} key={faq.id}>
              <button className="support-faq-question" type="button" aria-expanded={openFaq === faq.id} onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}>
                <span>{faq.question}</span><i className="ti ti-plus" aria-hidden="true" />
              </button>
              {openFaq === faq.id && <div className="support-faq-answer">
                <p>{faq.answer}</p>
                <div className="support-faq-feedback"><span>Was this helpful?</span><button type="button" disabled={sendingFeedback === faq.id} aria-pressed={faq.feedback === true} onClick={() => sendFeedback(faq, true)}><i className="ti ti-thumb-up" aria-hidden="true" /> Yes</button><button type="button" disabled={sendingFeedback === faq.id} aria-pressed={faq.feedback === false} onClick={() => sendFeedback(faq, false)}><i className="ti ti-thumb-down" aria-hidden="true" /> No</button><button type="button" className="support-text-link" onClick={() => { ticketFormRef.current?.scrollIntoView({ behavior: "smooth" }); setCategory(faq.category); }}>Still need help? Raise a ticket</button></div>
              </div>}
            </article>)}</div>
          </div>) : <div className="support-empty">{search ? "No articles match your search. Try another phrase or contact support." : "Help articles are being prepared. You can contact support below."}</div>}
      </section>

      <section className="support-contact-banner">
        <div className="support-contact-icon"><i className="ti ti-messages" aria-hidden="true" /></div>
        <div><h2>Still need a hand?</h2><p>Send our team a message and we’ll help you get back on track.</p></div>
        <button type="button" className="support-button support-button-primary" onClick={() => ticketFormRef.current?.scrollIntoView({ behavior: "smooth" })}>Raise a ticket <i className="ti ti-arrow-up-right" aria-hidden="true" /></button>
      </section>

      <section className="support-main-grid" ref={ticketFormRef}>
        <div className="support-card support-ticket-form-card">
          <div className="support-card-heading"><div><p className="support-eyebrow">CONTACT SUPPORT</p><h2>Raise a support ticket</h2><p>Share a few details so we can help as quickly as possible.</p></div><span className="support-heading-icon"><i className="ti ti-send" /></span></div>
          <form className="support-ticket-form" onSubmit={submitTicket}>
            <label>Subject <span>*</span><input required maxLength="180" value={subject} onChange={(event) => setSubject(event.target.value)} placeholder="What do you need help with?" /></label>
            <div className="support-form-row">
              <label>Category <span>*</span><select required value={category} onChange={(event) => setCategory(event.target.value)}><option value="">Select a category</option>{categories.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label>Priority<select value={priority} onChange={(event) => setPriority(event.target.value)}>{priorities.map((item) => <option key={item}>{item}</option>)}</select></label>
            </div>
            <label>Description <span>*</span><textarea required minLength="10" maxLength="10000" rows="6" value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Describe what happened and what you expected..." /><small>{description.length}/10,000 characters</small></label>
            <div className="support-attachment-field"><label htmlFor="support-ticket-files">Attachments <small>Optional · PNG, JPG, WebP, GIF, PDF, TXT or ZIP · up to 5 MB each, 12 MB total</small></label><input id="support-ticket-files" type="file" accept={supportFileAccept} multiple onChange={(event) => chooseFiles(event, "ticket")} />{files.length > 0 && <ul className="support-selected-files">{files.map((file, index) => <li key={`${file.name}-${index}`}><span><i className="ti ti-paperclip" />{file.name} <small>({(file.size / 1024 / 1024).toFixed(2)} MB)</small></span><button type="button" onClick={() => setFiles((items) => items.filter((_, fileIndex) => fileIndex !== index))} aria-label={`Remove ${file.name}`}><i className="ti ti-x" /></button></li>)}</ul>}</div>
            <button className="support-button support-button-primary support-submit" type="submit" disabled={submitting}>{submitting ? <><span className="support-spinner support-spinner-light" /> Submitting...</> : <>Submit ticket <i className="ti ti-arrow-right" aria-hidden="true" /></>}</button>
          </form>
        </div>

        <div className="support-card support-my-tickets">
          <div className="support-card-heading"><div><p className="support-eyebrow">YOUR SUPPORT HISTORY</p><h2>My tickets</h2><p>Follow conversations and ticket updates.</p></div><span className="support-ticket-count">{tickets.length}</span></div>
          {loading ? <TicketListSkeleton /> :
            tickets.length ? <div className="support-ticket-list">{tickets.map((ticket) => <button type="button" className={`support-ticket-row${selectedTicket === ticket.id ? " is-selected" : ""}`} key={ticket.id} onClick={() => openTicket(ticket.id)}>
              <span className="support-ticket-row-top"><strong>{ticket.ticketNumber}</strong><StatusBadge status={ticket.status} /></span><span className="support-ticket-subject">{ticket.subject}</span><span className="support-ticket-meta">{ticket.category} <span>·</span> <PriorityBadge priority={ticket.priority} /></span><small>{formatDate(ticket.createdAt)}</small>
            </button>)}</div> : <div className="support-empty support-ticket-empty"><span className="support-empty-icon"><i className="ti ti-inbox" /></span><strong>No tickets yet</strong><span>Your support conversations will appear here.</span></div>}
        </div>
      </section>

      {selectedTicket && <div className="support-detail-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelectedTicket(null); }}>
        <section className="support-detail-panel" role="dialog" aria-modal="true" aria-labelledby="support-detail-title">
          <header className="support-detail-header"><div><p className="support-eyebrow">SUPPORT CONVERSATION</p><h2 id="support-detail-title">{ticketDetails?.ticketNumber || "Ticket details"}</h2></div><button type="button" onClick={() => setSelectedTicket(null)} aria-label="Close ticket details"><i className="ti ti-x" /></button></header>
          {ticketLoading || !ticketDetails ? <ConversationSkeleton /> : <>
            <div className="support-detail-summary"><div><h3>{ticketDetails.subject}</h3><div className="support-ticket-meta">{ticketDetails.category} <span>·</span> <PriorityBadge priority={ticketDetails.priority} /></div></div><StatusBadge status={ticketDetails.status} /></div>
            <p className="support-detail-date">Created {formatDate(ticketDetails.createdAt)}</p>
            <div className="support-message-list">{(ticketDetails.replies || []).map((message) => <article className={`support-message${message.isAdmin ? " is-agent" : ""}`} key={message.id}><div className="support-message-top"><strong>{message.isAdmin ? (message.authorName || "Socialyorix Support") : "You"}</strong><time>{formatDate(message.createdAt)}</time></div><p>{message.message}</p>{message.attachments?.length > 0 && <div className="support-message-attachments">{message.attachments.map((attachment) => <button type="button" key={attachment.id} onClick={() => downloadSupportAttachment(attachment).catch((error) => showError(error.message))}><i className="ti ti-paperclip" />{attachment.name}</button>)}</div>}</article>)}</div>
            {!!ticketDetails.history?.length && <div className="support-user-history"><h3>Ticket history</h3><ol>{ticketDetails.history.map((event) => <li key={event.id}><span>{event.eventType === "created" ? "Ticket submitted" : event.eventType === "reply" ? event.newValue : `${event.eventType === "status" ? "Status" : event.eventType === "priority" ? "Priority" : "Assignment"}${event.oldValue ? ` changed from ${event.oldValue}` : ""}${event.newValue ? ` to ${event.newValue}` : ""}`}</span><time>{formatDate(event.createdAt)}</time></li>)}</ol></div>}
            {["Resolved", "Closed"].includes(ticketDetails.status) ? <div className="support-closed-note">This ticket is {ticketDetails.status.toLowerCase()}. If you still need help, raise a new ticket.</div> : <form className="support-reply-form" onSubmit={sendReply}><label htmlFor="support-reply">Reply to support</label><textarea id="support-reply" rows="4" maxLength="10000" value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Write your message..." required /><input aria-label="Add attachments to reply" type="file" accept={supportFileAccept} multiple onChange={(event) => chooseFiles(event, "reply")} />{replyFiles.length > 0 && <div className="support-reply-files">{replyFiles.map((file, index) => <span key={`${file.name}-${index}`}>{file.name}<button type="button" onClick={() => setReplyFiles((items) => items.filter((_, fileIndex) => fileIndex !== index))} aria-label={`Remove ${file.name}`}><i className="ti ti-x" /></button></span>)}</div>}<button className="support-button support-button-primary" type="submit" disabled={replying}>{replying ? "Sending..." : "Send reply"} <i className="ti ti-send" /></button></form>}
          </>}
        </section>
      </div>}
    </section>
  );
};

export default HelpSupport;
